import { pluralize } from "@common/helpers/string";
import { Invoice, InvoiceLineItem } from "@common/types/invoice";
import { InvoiceLineItemView } from "@common/types/views/invoiceViews";
import { LineItemsCommandIslandFooter } from "@components/lineItems/LineItemsCommandIslandFooter";
import React, { useMemo } from "react";
import { InvoiceLineItemsView } from "@components/invoices/views/invoiceLineItemsView/InvoiceLineItemsView";
import { InvoiceLineItemsViewCallbacks } from "@components/invoices/views/invoiceLineItemsView/types";
import { useInvoicesView } from "../../../../contexts/InvoicesViewContext";
import { PaginatedInvoicesTableContext } from "../../PaginatedInvoicesTable.context";
import { useInvoiceTableEmptyView } from "../../useInvoiceTableEmptyView";

interface InvoiceLineItemsViewContainerProps {
    view: InvoiceLineItemView;
    hasAnyAccrualLedgers: boolean;
    isSchedulesEnabled?: boolean;
    prioritizedScheduleTemplateId?: string;
    onCreateSchedule?: InvoiceLineItemsViewCallbacks["onCreateSchedule"];
    onScheduleApplied?: InvoiceLineItemsViewCallbacks["onScheduleApplied"];
    onRequestScheduleConfirm?: InvoiceLineItemsViewCallbacks["onRequestScheduleConfirm"];
    onRequestLifecycleAction?: InvoiceLineItemsViewCallbacks["onRequestLifecycleAction"];
    onInvoiceClick: (invoice: Invoice) => void;
    selectedInvoiceId?: string;
    selectedLineItems?: Record<string, InvoiceLineItem>;
    hasSelectedByCurrentFilters?: boolean;
    onSelectedLineItemsChange?: (lineItems: InvoiceLineItem[]) => void;
    selectedCount: number;
    hasSelection: boolean;
    onClearSelection: () => void;
    onSelectAllByFilters: () => void;
    onOpenBulkActions: () => void;
    hasConnectedInvoiceAccounts: boolean;
    onClearFilters: () => void;
}

export const InvoiceLineItemsViewContainer: React.FC<
    InvoiceLineItemsViewContainerProps
> = ({
    view,
    hasAnyAccrualLedgers,
    isSchedulesEnabled = false,
    prioritizedScheduleTemplateId,
    onCreateSchedule,
    onScheduleApplied,
    onRequestScheduleConfirm,
    onRequestLifecycleAction,
    onInvoiceClick,
    selectedInvoiceId,
    selectedLineItems,
    hasSelectedByCurrentFilters = false,
    onSelectedLineItemsChange,
    selectedCount,
    hasSelection,
    onClearSelection,
    onSelectAllByFilters,
    onOpenBulkActions,
    hasConnectedInvoiceAccounts,
    onClearFilters,
}) => {
    const {
        invoices,
        invoiceCount,
        currentSort,
        handleSortChange,
        currentPage,
        totalPages,
        setPage,
        isLoading,
    } = React.useContext(PaginatedInvoicesTableContext);
    const { invoiceStats } = useInvoicesView();

    const { emptyView, shouldHideFooter } = useInvoiceTableEmptyView({
        isLoading,
        filteredCount: invoiceCount,
        hasConnectedInvoiceAccounts,
        onClearFilters,
        viewKind: "lineItems",
    });

    const stats = useMemo(() => {
        if (!invoiceStats) {
            return undefined;
        }
        const totalOpenAmount = invoiceStats.open.total;
        const totalPaidAmount = invoiceStats.paid.total;
        return {
            totalOpenAmount,
            totalPaidAmount,
            totalAmount: totalOpenAmount + totalPaidAmount,
            count: invoiceCount,
            countLabel: pluralize("Invoice", invoiceCount),
        };
    }, [invoiceStats, invoiceCount]);

    const footer = (
        <LineItemsCommandIslandFooter
            totalLabel={`${invoiceCount} ${pluralize("invoice", invoiceCount)}`}
            totalPages={totalPages}
            currentPage={currentPage}
            onPageChange={setPage}
            selectedCount={selectedCount}
            hasSelection={hasSelection}
            hasSelectedByCurrentFilters={hasSelectedByCurrentFilters}
            onClearSelection={onClearSelection}
            onSelectAllByFilters={onSelectAllByFilters}
            onOpenBulkActions={onOpenBulkActions}
            stats={stats}
        />
    );

    return (
        <InvoiceLineItemsView
            view={view}
            hasAnyAccrualLedgers={hasAnyAccrualLedgers}
            isSchedulesEnabled={isSchedulesEnabled}
            prioritizedScheduleTemplateId={prioritizedScheduleTemplateId}
            onCreateSchedule={onCreateSchedule}
            onScheduleApplied={onScheduleApplied}
            onRequestScheduleConfirm={onRequestScheduleConfirm}
            onRequestLifecycleAction={onRequestLifecycleAction}
            invoices={invoices ?? null}
            selectedInvoiceId={selectedInvoiceId}
            onInvoiceClick={onInvoiceClick}
            sort={currentSort}
            onSortChange={handleSortChange}
            footer={shouldHideFooter ? undefined : footer}
            selectedLineItems={selectedLineItems}
            hasSelectedByCurrentFilters={hasSelectedByCurrentFilters}
            onSelectedLineItemsChange={onSelectedLineItemsChange}
            emptyView={emptyView}
        />
    );
};
